import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, MapPin, CheckCircle, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PROPERTIES } from '@/data/index';

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState(0);

  const property = PROPERTIES.find((p) => p.id === id);

  if (!property) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background">
          <h1 className="text-3xl font-bold text-foreground">Property Not Found</h1>
          <p className="text-muted-foreground">The listing you are looking for does not exist.</p>
          <Button onClick={() => navigate('/properties')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Properties
          </Button>
        </div>
      </Layout>
    );
  }

  const images = property.images.length > 0 ? property.images : ['/images/prop1.jpg'];
  const hasMultiple = images.length > 1;

  const prev = () => setActiveImg((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setActiveImg((i) => (i === images.length - 1 ? 0 : i + 1));

  return (
    <Layout>
      {/* ── GALLERY ── */}
      <div className="w-full bg-background pt-4">
        {/* Main image */}
        <div className="relative w-full h-[55vw] max-h-[520px] min-h-[260px] overflow-hidden bg-black">
          <img
            key={activeImg}
            src={images[activeImg]}
            alt={`${property.title} – photo ${activeImg + 1}`}
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* Dark overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent pointer-events-none" />

          {/* Back button */}
          <div className="absolute top-4 left-4 z-20">
            <Button
              variant="outline"
              size="sm"
              className="bg-primary/80 border-accent/40 text-primary-foreground backdrop-blur-sm hover:bg-primary"
              onClick={() => navigate('/properties')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Properties
            </Button>
          </div>

          {/* Type / badge */}
          <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 items-end">
            <Badge className="bg-accent text-accent-foreground text-sm px-3 py-1">
              {property.type}
            </Badge>
            {property.badge && (
              <Badge className="bg-secondary text-secondary-foreground text-sm px-3 py-1">
                {property.badge}
              </Badge>
            )}
          </div>

          {/* Prev / Next arrows – only shown when multiple images */}
          {hasMultiple && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/75 transition"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/75 transition"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Dot indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    aria-label={`Go to image ${i + 1}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      i === activeImg ? 'bg-accent scale-125' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail strip – only when multiple images */}
        {hasMultiple && (
          <div className="flex gap-2 overflow-x-auto px-4 py-3 bg-card border-b border-border scrollbar-thin">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  i === activeImg ? 'border-accent' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={src}
                  alt={`Thumbnail ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-card border border-border rounded-2xl p-6 md:p-12 shadow-2xl shadow-black/30"
        >
          {/* Title & Price */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-3 leading-tight">
                {property.title}
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 text-accent" />
                <span>{property.location}</span>
              </div>
            </div>
            <div className="text-left md:text-right flex-shrink-0">
              <p className="text-sm text-muted-foreground mb-1">Listing Price</p>
              <p className="font-mono text-3xl md:text-4xl font-bold text-accent">
                {property.price > 0 ? `$${property.price.toLocaleString()}` : 'Price on Request'}
              </p>
              <div className="flex items-center gap-2 mt-2 md:justify-end">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{property.category}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-border mb-8" />

          {/* Description */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-foreground mb-4">About This Property</h2>
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
              {property.description}
            </p>
          </div>

          {/* Features */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-foreground mb-4">Key Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {property.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status + CTA */}
          <div className="border-t border-border pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Badge
                className={
                  property.status === 'Available'
                    ? 'bg-green-600/20 text-green-400 border-green-600/30'
                    : property.status === 'Reserved'
                    ? 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30'
                    : 'bg-red-600/20 text-red-400 border-red-600/30'
                }
                variant="outline"
              >
                {property.status}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button
                variant="outline"
                className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                onClick={() => navigate('/contact')}
              >
                Enquire About This Property
              </Button>
              <Button
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => navigate('/properties')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to All Listings
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
